<template>
  <div>
    <div
      v-if="!loadingError"
      class="text-right mb-2"
    >
      <b-button
        variant="outline-primary"
        class="refresh-btn mr-1"
        :disabled="loading"
        @click="fetchItems"
      >
        Refresh
      </b-button>
      <b-button
        variant="outline-primary"
        class="mr-1"
        :disabled="!selectedItems.length"
        @click="displayReCreateModal = true"
      >
        Re-create on Marg
      </b-button>
      <b-button
        variant="outline-primary"
        class="mr-1"
        :disabled="isDownloadingDisabled"
        @click="downloadOrders([...selectedItems])"
      >
        Download Excel
      </b-button>
      <b-button
        variant="outline-primary"
        :disabled="!selectedItems.length || downloadingPdf"
        @click="downloadPdf([...selectedItems])"
      >
        Download PDF
      </b-button>
    </div>

    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ loadingError }}
        </p>
      </div>
    </b-alert>

    <template v-if="!loading && !loadingError">
      <div class="table-responsive-horizontal">
        <b-table
          :items="results"
          :fields="tableFields"
          responsive="sm"
          striped
          hover
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          class="compact-table"
          no-local-sorting
          show-empty
          empty-text="No Records to display!"
          @sort-changed="sortingChanged"
        >
          <template #head(select)>
            <b-form-checkbox
              v-model="isAllItemsSelected"
              :disabled="results.length === 0"
              @change="handleAllItemsSelectChange"
            />
          </template>
          <template #cell(select)="value">
            <b-form-checkbox
              v-model="selectedItems"
              :value="value.item.id"
              name="select-status"
            />
          </template>
          <template #cell(total_items)="data">
            {{ formateNumber(data.item.total_items) }}
          </template>
          <template #cell(total_amount)="data">
            {{ formateNumber(data.item.total_amount) }}
          </template>
          <template #cell(created_at)="data">
            {{ data.item.created_at ? formatedDate(data.item.created_at, 'DD-MM-YYYY LT') : '-' }}
          </template>
          <template #cell(status)="value">
            <StatusChip :data="value.item.status" />
          </template>
          <template #cell(bill_no)="data">
            {{ data.item.bill_info ? data.item.bill_info.bill.bill_no : '' }}
          </template>
          <!-- Add color coding for percentage difference -->
          <template #cell(percentage_difference)="data">
            <span
              v-if="data.item.percentage_difference !== null && data.item.percentage_difference !== undefined"
              :class="getPercentageDifferenceClass(data.item.percentage_difference)"
            >
              {{ data.item.percentage_difference }}%
            </span>
            <span v-else>-</span>
          </template>
          <template #cell(remarks)="data">
            <feather-icon
              v-if="data.item.remarks"
              v-b-tooltip.hover="{boundary:'window'}"
              :title="data.item.remarks"
              icon="MessageSquareIcon"
              size="20"
              class="cursor-pointer mr-1"
            />
          </template>
          <template #cell(bill_amount)="data">
            {{ data.item.bill_info ? formateNumber(data.item.bill_info.bill.amount) : '' }}
          </template>
          <template #cell(actions)="data">
            <feather-icon
              v-b-tooltip.hover="{boundary:'window'}"
              title="View Details"
              icon="EyeIcon"
              size="20"
              class="cursor-pointer mr-1"
              @click="displayDetails(data.item.id)"
            />

            <feather-icon
              v-if="data.item.is_downloaded"
              v-b-tooltip.hover="{boundary:'window'}"
              :title="getDownloadTooltip(data.item)"
              icon="DownloadIcon"
              size="20"
              class="cursor-pointer mr-1 text-warning"
              @click="downloadOrders([data.item.id])"
            />

            <feather-icon
              v-if="data.item.pdf_downloaded_at"
              v-b-tooltip.hover="{boundary:'window'}"
              :title="getPdfDownloadTooltip(data.item)"
              icon="FileTextIcon"
              size="20"
              class="cursor-pointer mr-1 text-warning"
              @click="downloadPdf([data.item.id])"
            />
          </template>
        </b-table>
      </div>
      <detailed-pagination
        :total-items="totalItems"
        :per-page="perPage"
        :current-page="currentPage"
        @page-changed="(page) => {currentPage = page}"
      />
    </template>

    <order-details
      v-if="displayOrderDetailsId"
      :item-id="displayOrderDetailsId"
      @modal-closed="displayOrderDetailsId=null"
      @pdf-downloaded="markPdfDownloaded"
    />

    <recreate-on-marg-modal
      v-if="displayReCreateModal"
      :item-ids="selectedItems"
      @modal-closed="displayReCreateModal = false"
      @updated="onRecreateUpdate"
    />

  </div>
</template>

<script>
import {
  BSpinner, BTable, BAlert, VBTooltip, BButton, BFormCheckbox,
} from 'bootstrap-vue'
import axios from 'axios'

import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import orderPdfMixin from '@/mixins/orderPdfMixin'
import DetailedPagination from '@/components/UI/DetailedPagination.vue'
import StatusChip from '@/components/UI/StatusChip.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import download from 'downloadjs'
import OrderDetails from './OrderDetails.vue'
import RecreateOnMargModal from './RecreateOnMargModal.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BSpinner,
    BTable,
    BAlert,
    OrderDetails,
    DetailedPagination,
    StatusChip,
    BButton,
    BFormCheckbox,
    RecreateOnMargModal,
  },
  mixins: [dateMixin, formatMixin, orderPdfMixin],
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      loadingError: null,
      results: [],
      tableFields: [
        { key: 'select' },
        { key: 'order_number', label: 'Order Number', sortable: true },
        { key: 'marg_order_id', label: 'Marg Order ID', sortable: true },
        { key: 'party__identifier', label: 'Party', sortable: true },
        {
          key: 'total_items', label: 'Total Items', sortable: true, class: 'text-right pr-2',
        },
        {
          key: 'total_amount', label: 'Order Amount', sortable: true, class: 'text-right pr-2',
        },
        { key: 'placed_by__fullname', label: 'Placed By', sortable: true },
        { key: 'created_at', label: 'Created At', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'bill_no', label: 'Bill No.' },
        { key: 'bill_amount', label: 'Bill Amount', class: 'text-right pr-2' },
        { key: 'percentage_difference', label: '%Diff Amount' },
        { key: 'remarks', label: 'Remarks', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      currentPage: 1,
      perPage: 50,
      totalItems: 0,
      sortBy: 'created_at',
      sortDesc: true,
      displayOrderDetailsId: null,
      selectedItems: [],
      isAllItemsSelected: false,
      displayReCreateModal: false,
      downloading: false,
    }
  },
  computed: {
    isDownloadingDisabled() {
      const isDownloadedItemSelected = this.results.some(item => item.is_downloaded && this.selectedItems.includes(item.id))

      return isDownloadedItemSelected || !this.selectedItems.length || this.downloading
    },
  },
  watch: {
    currentPage() {
      this.fetchItems()
    },
    filters() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        this.fetchItems()
      }
    },
    selectedItems(newVal) {
      if (this.results.length > 0 && newVal.length === this.results.length) {
        this.isAllItemsSelected = true
      } else {
        this.isAllItemsSelected = false
      }
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.fetchItems()
    },
    getPercentageDifferenceClass(percentage) {
      const absPercentage = Math.abs(percentage)
      if (absPercentage <= 5) {
        return 'text-success font-weight-bold' // Green for <= 10%
      }
      return 'text-danger font-weight-bold' // Red for > 10%
    },
    getDownloadTooltip(item) {
      if (item.downloaded_by__fullname && item.downloaded_at) {
        return `Downloaded by ${item.downloaded_by__fullname} on ${this.formatedDate(item.downloaded_at, 'DD-MM-YYYY hh:mm A')}`
      }
      return 'Re-Download'
    },
    fetchItems() {
      this.loading = true
      axios.post('/marg/filtered_orders',
        {
          page: this.currentPage,
          per_page: this.perPage,
          sort_by: this.sortBy,
          sort_desc: this.sortDesc,
          retailers_only: false,
          filters: {
            ...this.filters,
          },
        })
        .then(res => {
          this.results = this.calculatePercentageDifference(res.data.results)
          this.totalItems = res.data.count
          this.loadingError = null
          this.loading = false
        }).catch(error => {
          this.loadingError = error?.response?.data?.error || 'Error fetching order items!'
          this.loading = false
        })
    },
    displayDetails(itemId) {
      this.displayOrderDetailsId = itemId
    },
    handleAllItemsSelectChange(isChecked) {
      this.selectedItems = isChecked
        ? this.results.map(data => data.id)
        : []
    },
    onRecreateUpdate() {
      this.selectedItems = []
      this.fetchItems()
    },
    calculatePercentageDifference(ordersArray) {
      return ordersArray.map(order => {
        const billAmount = order.bill_info?.bill?.amount
        const billOrdersTotal = order.bill_orders_total

        // No bill yet, or no total from backend -> show "-"
        if (billAmount === null || billAmount === undefined
          || billOrdersTotal === null || billOrdersTotal === undefined) {
          return { ...order, percentage_difference: null }
        }

        const percentageDifference = billOrdersTotal !== 0
          ? ((billAmount - billOrdersTotal) / billOrdersTotal) * 100
          : 0

        return {
          ...order,
          percentage_difference: Math.ceil(percentageDifference),
        }
      })
    },
    async downloadPdf(ids) {
      const downloaded = await this.downloadOrdersPdf(ids)
      if (!downloaded) return

      this.markPdfDownloaded(ids)
      this.selectedItems = []
    },
    markPdfDownloaded(ids) {
      // First download info is preserved on re-download
      const userName = this.$store.getters['auth/userName']
      const downloadedAt = new Date().toISOString()
      this.results = this.results.map(item => {
        if (ids.includes(item.id) && !item.pdf_downloaded_at) {
          return {
            ...item,
            pdf_downloaded_at: downloadedAt,
            pdf_downloaded_by__fullname: userName,
          }
        }
        return item
      })
    },
    downloadOrders(ids) {
      this.downloading = true

      axios.get('/marg/download_orders', {
        params: { order_ids: JSON.stringify(ids) },
        responseType: 'blob',
      })
        .then(res => {
          const contentType = res.headers['content-type']
          download(res.data, 'Marg Order', contentType)

          // Mark items as downloaded
          const userName = this.$store.getters['auth/userName']
          const downloadedAt = new Date().toISOString()
          this.results = this.results.map(item => {
            if (ids.includes(item.id) && !item.is_downloaded) {
              return {
                ...item,
                is_downloaded: true,
                downloaded_by__fullname: userName,
                downloaded_at: downloadedAt,
              }
            }
            return item
          })

          this.downloading = false
          this.selectedItems = []
        }).catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error?.response?.data?.error || 'Something went wrong!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
          this.downloading = false
        })
    },
  },
}
</script>
<style scoped>
.table-responsive-horizontal {
  overflow-x: auto;
}
</style>
